import { useEffect, useRef } from 'react';

import { useGetResources } from '@hooks/useGetResources';
import { EMPTY_GUID } from '@src/constants/guids';
import { LenkeType } from '@src/types/minhelseentities';
import { useAppDispatch, useAppSelector } from '@store';
import { clearError } from '@store/verktoy/slice/verktoySlice';
import { fetchMittVerktoyByGuidThunk, fetchVerktoyThunk } from '@store/verktoy/thunks';
import { sporAapningAvVerktoy, sporAapningAvVerktoyFraLenke } from '@utils/amplitude-utils';
import { launchAktivitet } from '@utils/launch-aktivitet';
import { erVerktoyTilgjengelig } from '@utils/utils';
import { useNavigate, useParams } from 'react-router';

import Loader from '@helsenorge/designsystem-react/components/Loader';

import PageRoot from '@helsenorge/core-framework/pageroot';
import { isAuthorized } from '@helsenorge/framework-utils/hn-authorize';

const UthoppLandingPage = (): React.JSX.Element | null => {
  const { verktoyId, laeringsaktivitetGuid: guid } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const resources = useGetResources();
  const verktoy = useAppSelector(state => state.verktoy.verktoy);
  const verktoyLoading = useAppSelector(state => state.verktoy.verktoyLoading);
  const mittVerktoyByGuid = useAppSelector(state => state.verktoy.mittVerktoyByGuid);
  const mittVerktoyByGuidLoading = useAppSelector(state => state.verktoy.mittVerktoyByGuidLoading);
  const error = useAppSelector(state => state.verktoy.error);
  const hasLaunched = useRef(false);

  useEffect(() => {
    if (!isAuthorized()) {
      navigate(verktoyId ? `/${verktoyId}` : '/', { replace: true });
      return;
    }
    if (guid && guid !== EMPTY_GUID) {
      dispatch(fetchMittVerktoyByGuidThunk({ laeringsressursGuid: guid }));
    } else if (verktoyId) {
      dispatch(fetchVerktoyThunk({ isLoggedIn: true, verktoyId }));
    }
  }, [dispatch, verktoyId, guid, navigate]);

  useEffect(() => {
    if (!verktoyId || verktoyLoading || !verktoy || hasLaunched.current) {
      return;
    }

    hasLaunched.current = true;
    if (erVerktoyTilgjengelig(verktoy)) {
      sporAapningAvVerktoyFraLenke(verktoy, LenkeType.Standard);
      launchAktivitet({
        verktoyId: verktoy.VerktoyId,
        oppstartsType: verktoy.OppstartType,
        lenker: verktoy.Lenker,
        lenkeType: LenkeType.Standard,
        kanStartesAvInnloggetBruker: verktoy.KanStartesAvInnloggetBruker,
        uthopp: true,
      });
    } else {
      navigate(`/${verktoy.VerktoyId}`, { replace: true });
    }
  }, [verktoyId, verktoy, verktoyLoading, navigate]);

  useEffect(() => {
    if (!guid || mittVerktoyByGuidLoading || !mittVerktoyByGuid || hasLaunched.current) {
      return;
    }

    hasLaunched.current = true;
    if (mittVerktoyByGuid.ErVerktoyTilgjengelig) {
      sporAapningAvVerktoy(mittVerktoyByGuid.VerktoyNavn, LenkeType.Standard, mittVerktoyByGuid.InterntVerktoy);
      launchAktivitet({
        verktoyId: mittVerktoyByGuid.VerktoyId,
        oppstartsType: mittVerktoyByGuid.OppstartType,
        lenker: mittVerktoyByGuid.Lenker,
        lenkeType: LenkeType.Standard,
        kanStartesAvInnloggetBruker: mittVerktoyByGuid.KanStartesAvInnloggetBruker,
        uthopp: true,
      });
    } else {
      navigate(`/${mittVerktoyByGuid.VerktoyId}`, { replace: true });
    }
  }, [guid, mittVerktoyByGuid, mittVerktoyByGuidLoading, navigate]);

  useEffect(() => {
    if (error) {
      dispatch(clearError());
      navigate(verktoyId ? `/${verktoyId}` : '/', { replace: true });
    }
  }, [error, verktoyId, navigate, dispatch]);

  if (!resources) {
    return null;
  }

  return (
    <PageRoot hasPageHeader={false} pageTitle={resources.entryPage_header} enablePageviewTracking enableSurveys>
      <div className="verktoy__wrapper">
        <Loader />
      </div>
    </PageRoot>
  );
};

export default UthoppLandingPage;
